import React from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
// Utils
import { getItem, removeItem } from '@sketch/utils'
import { isPaymentMethodInvoice } from 'modules/workspace/utils'

// Constants
import { localStorageKeys } from '@sketch/constants'
import { routes } from '@sketch/modules-common'
import { SKETCH_WEBSITE } from '@sketch/env-config'

// Components
import { Button, LinkButton } from '@sketch/components'

import { useToast } from '@sketch/toasts'

// Local Components
import EmptyState from '../EmptyState'

// GQL
import {
  GetWorkspacesDocument,
  useDeleteWorkspaceMutation,
  WorkspaceMinimalFragment,
  useGetPaymentDetailsQuery,
  PaymentDetailsFragment,
} from '@sketch/gql-types'

// Operations
const invoiceWSCopyDelete =
  'To add documents, contact us to renew your subscription and activate the Workspace again. Alternatively, delete the Workspace if you no longer need it.'
const renewCopyDelete =
  'To add documents, renew your subscription to activate the Workspace again. Alternatively, delete the Workspace if you no longer need it.'
const copyMembersNoDelete =
  'To add documents, an Admin needs to renew the subscription — which will activate the Workspace again.'

const StyledLinkButton = styled(LinkButton)`
  color: ${({ theme }) => theme.colors.foreground.primary.A};
  &:hover {
    color: ${({ theme }) => theme.colors.foreground.primary.A};
  }
`

interface ExpiredWorkspaceActionsProps {
  workspaceId: string
  canRenewSubscription: boolean
  canDeleteWorkspace: boolean
  showContactUs: boolean
}

const ExpiredWorkspaceActions: React.FC<ExpiredWorkspaceActionsProps> = ({
  workspaceId,
  canRenewSubscription,
  canDeleteWorkspace,
  showContactUs,
}) => {
  const history = useHistory()
  const { showToast } = useToast()

  const [deleteWorkspace, { loading }] = useDeleteWorkspaceMutation({
    variables: { workspaceId },
    onError: 'show-toast',
    onCompleted: data => {
      const { deletedWorkspace } = data.deleteWorkspace || {}

      // Remove last visited workspace from local storage
      const storedWorkspaceId = getItem(localStorageKeys.lastWorkspaceIdKey)

      if (storedWorkspaceId === deletedWorkspace?.identifier) {
        removeItem(localStorageKeys.lastWorkspaceIdKey)
      }

      showToast('Your workspace was successfully deleted.', 'positive')
      history.push(routes.ENTRY.create({}))
    },
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GetWorkspacesDocument }],
  })

  return (
    <>
      {canDeleteWorkspace && (
        <Button
          variant="negative-secondary"
          onClick={() => deleteWorkspace()}
          loading={loading}
        >
          Delete Workspace…
        </Button>
      )}
      {canRenewSubscription && (
        <Button
          variant="primary"
          onClick={() =>
            history.push(
              routes.WORKSPACE_SUBSCRIBE.create({
                workspaceId,
              })
            )
          }
          disabled={loading}
        >
          Renew Subscription…
        </Button>
      )}
      {showContactUs && (
        <StyledLinkButton
          external
          variant="primary"
          href={`${SKETCH_WEBSITE}/support/contact/`}
        >
          Contact Us…
        </StyledLinkButton>
      )}
    </>
  )
}

interface RenewSubEmptyStateProps {
  workspace: WorkspaceMinimalFragment
}

const getPermissions = (
  workspace: WorkspaceMinimalFragment,
  paymentDetails?: PaymentDetailsFragment
) => {
  const { userRole } = workspace
  const isInvoice = paymentDetails && isPaymentMethodInvoice(paymentDetails)

  const canAdminister = userRole === 'ADMIN' || userRole === 'FINANCE'

  const canDeleteWorkspace = userRole === 'ADMIN'
  const canRenewSubscription = !isInvoice && canAdminister
  const showContactUs = !!isInvoice && canAdminister

  return {
    canRenewSubscription,
    showContactUs,
    canDeleteWorkspace,
  }
}

const RenewSubEmptyState: React.FC<RenewSubEmptyStateProps> = ({
  workspace,
}) => {
  const { identifier } = workspace
  const { data, loading } = useGetPaymentDetailsQuery({
    variables: { customerId: workspace.customer!.identifier },
    fetchPolicy: 'network-only',
  })

  if (loading) return null

  const paymentDetails = data?.customer?.paymentDetails
  const {
    canRenewSubscription,
    canDeleteWorkspace,
    showContactUs,
  } = getPermissions(workspace, paymentDetails)

  let copy = canRenewSubscription ? renewCopyDelete : invoiceWSCopyDelete

  if (!canDeleteWorkspace) copy = copyMembersNoDelete

  return (
    <EmptyState
      icon="document"
      title="Your Workspace doesn’t have any documents yet"
      description={<>{copy}</>}
      callToAction={
        <ExpiredWorkspaceActions
          workspaceId={identifier}
          canRenewSubscription={canRenewSubscription}
          canDeleteWorkspace={canDeleteWorkspace}
          showContactUs={showContactUs}
        />
      }
    />
  )
}

export default RenewSubEmptyState
